import { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AsistenciaMedicaContext } from "../contexts/AsistenciaMedicaContext";
import { AsistenciaMedicaActionType } from "../interfaces/global-state/asistencia.medica.action.type.enum";
import { controlDeVerdes } from "../helpers/controlVerdesHelper";
import { filtrarCasosEnCurso, filtrarCasosEnHistorial } from "../helpers/serviciosEnCursoHelper";
import { getCasosEnCurso } from "../api/salusService";
import { DELAY_REQUEST_CASOS_EN_CURSO, DETALLE_CASO, HOME, SEGUIMIENTO_VACIO } from "../utils/constants";
import { reintentarSolicitudesInicio } from "../helpers/reintentarHelper";
import { getDeshabilitacionModuloFromBackend } from "../api/deshabilitacionModuloService";
import { getTextosEditables, loadCanales, loadDeshabilitarModulosPorVersiones, loadServicios, loadSocioPrincipal } from "../contexts/actions/actions";
import { DataControlDeVersiones } from "../interfaces/ads/control.versiones.interface";
import { serviciosHabilitados } from "../helpers/serviciosHelper";
import { Flags } from "../interfaces/home.interface";

export default function useHomeEffects(state, socioPrincipal, setSocioPrincipal, reintentarError, setReintentarError, setError) {

    const history = useHistory();
    const { useRequest, asistenciaMedica, dispatchAsistenciaMedica, storageManager, user, dataControlDeVersiones, isWeb } = useContext(AsistenciaMedicaContext);
    const { apiRequest } = useRequest();
    const controlVersiones: DataControlDeVersiones = dataControlDeVersiones;
    const stateFromAds = (state?.from === "/telefonos" || state?.from === "/homePpal" || state?.from === "/micuenta");
    const asistenciaDisable = controlVersiones?.asistencia?.infoModulo?.enabled !== undefined ? !controlVersiones?.asistencia?.infoModulo?.enabled : false;
    const [deshabilitarAsistencia, setDeshabilitarAsistencia] = useState(asistenciaDisable);
    const [requestServicio, setRequestServicio] = useState(false);
    const [skeletonSeguimiento, setSkeletonSeguimiento] = useState(false);
    const [asistenciaSkeleton, setAsistenciaSkeleton] = useState(stateFromAds);
    const [reintentarRequest, setReintentarRequest] = useState(false);
    const [deshabilitarModulos, setDeshabilitarModulos] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);
    // El state llamarServicios hace referencia a los llamados de servicio en inicio, luego de verificar si se encuentra deshabilitado el modulo de ADS
    const [llamarServicios, setLlamarServicios] = useState(false);
    const tipoSocio = asistenciaMedica?.tipoSocio;

    const flags = useMemo<Flags>(() => ({
        skeletonSeguimiento,
        asistenciaSkeleton,
        deshabilitarAsistencia,
        requestServicio
    }), [skeletonSeguimiento, asistenciaSkeleton, deshabilitarAsistencia, requestServicio]);

    useEffect(() => {
        if (stateFromAds || isWeb) {
            Promise.all([
                loadSocioPrincipal(apiRequest, user, tipoSocio, dispatchAsistenciaMedica),
                getTextosEditables(dispatchAsistenciaMedica, apiRequest),
                loadDeshabilitarModulosPorVersiones(isWeb, apiRequest, dispatchAsistenciaMedica),
                loadCanales(apiRequest, dispatchAsistenciaMedica, storageManager),
                loadServicios(apiRequest, storageManager, dispatchAsistenciaMedica)
            ]).then(() => {
                console.info("ASIS-MED - Ya se encuentra inicializado el socio Principal, los listados globales de Provincias, Factores y Sintomas.");
                setDataLoaded(true);
            }).catch(error => {
                console.error("ASIS-MED - Ocurrio un error al inicializar canales y servicios: ", error);
                setDataLoaded(false);
                setReintentarError(true);
            });
        }
    }, []);

    useEffect(() => {
        if (dataLoaded && llamarServicios) {
            setAsistenciaSkeleton(false);
            if (!deshabilitarAsistencia) {
                if (state && asistenciaMedica.usuario || isWeb) {
                    if (stateFromAds) {
                        storageManager.removeDataKeyFromStorage('fechaRequestCasosEnCurso').then(() => {
                            solicitarServiciosEnCurso()
                        })
                    } else if (state?.seguimiento) {
                        setSkeletonSeguimiento(true);
                        solicitarServiciosEnCurso()
                            .then((casos) => {
                                if (casos.length > 0) {
                                    let casoSeguimiento = casos.find((caso) => caso?.nroCaso === state?.nroCaso);
                                    casoSeguimiento ?
                                        history.push(DETALLE_CASO, { casoEnCursoSeleccionado: casoSeguimiento, from: HOME })
                                        : setError(true);
                                } else {
                                    //se redirecciona a la page de advertencia de recuperacion de datos
                                    history.push(SEGUIMIENTO_VACIO);
                                }
                            })
                            .catch((error) => console.error("Hubo un problema al solicitar servicios en curso: ", error))
                            .finally(() => setSkeletonSeguimiento(false))
                    } else {
                        solicitarServiciosEnCurso();
                    }
                }
            }
        }
    }, [dataLoaded, llamarServicios])

    useEffect(() => {
        if (!deshabilitarAsistencia) {
            if (reintentarRequest) {
                setAsistenciaSkeleton(true);
                reintentarSolicitudesInicio(user, isWeb, apiRequest, dispatchAsistenciaMedica, storageManager).then(socio => {
                    setSocioPrincipal(socio);
                    setAsistenciaSkeleton(false);
                }).catch(() => {
                    setAsistenciaSkeleton(false);
                    setReintentarError(true);
                });
                setReintentarRequest(false);
            }
        }
    }, [reintentarRequest])

    useEffect(() => {
        if (reintentarError) setAsistenciaSkeleton(false);
        else if (!reintentarError && asistenciaMedica?.usuario) {
            if (deshabilitarModulos === null) return controlDeVersiones();
            setSocioPrincipal(asistenciaMedica?.usuario);
            setDataLoaded(true);
        }
    }, [reintentarError, asistenciaMedica])

    useEffect(() => {
        getDeshabilitacionModuloFromBackend(apiRequest).then(deshabilitacion => {
            storageManager.setDataStorage("deshabilitacion", JSON.stringify(deshabilitacion));
            const deshabilitar = isWeb ? deshabilitacion?.deshabilitarWeb : deshabilitacion?.deshabilitarAds;
            if (deshabilitar) {
                setDeshabilitarModulos({ ...deshabilitacion });
                setDeshabilitarAsistencia(deshabilitar);
                setLlamarServicios(true);
            } else if (stateFromAds) {
                controlDeVersiones();
            } else if (isWeb || state?.seguimiento) {
                setLlamarServicios(true);
            }
        }).catch(error => {
            console.error("ASIS-MED - Ocurrio un error al realizar el request de deshabilitacion del módulo: ", error);
            if (stateFromAds) controlDeVersiones();
            setDeshabilitarModulos(null);
        });
    }, []);

    /**
     * Encargado de las peticiones de servicios en curso y el historial de atenciones
     */
    const solicitarServiciosEnCurso = () => {
        return new Promise<any>(async (resolve, reject) => {
            let fechaRequestStorage;
            let casosFiltrados;
            let serviciosActivos;
            setRequestServicio(true);

            try {
                serviciosActivos = await serviciosHabilitados(apiRequest);
            } catch (error: any) {
                console.error(`Hubo un error al recuperar los servicios: ${error}`);
            }

            storageManager.getDataStorage("fechaRequestCasosEnCurso").then(async fecha => {
                fechaRequestStorage = fecha;

                let fechaRequestCasosEnCurso = new Date(fechaRequestStorage);
                let fechaActual = new Date();
                let casosPorClasificacion;

                fechaRequestCasosEnCurso.setMinutes(fechaRequestCasosEnCurso.getMinutes() + DELAY_REQUEST_CASOS_EN_CURSO);
                if (fechaRequestStorage === undefined || fechaRequestCasosEnCurso.getTime() <= fechaActual.getTime()) {
                    try {
                        const casos = await getCasosEnCurso(asistenciaMedica?.usuario?.ic, apiRequest);
                        let fechaAct = new Date();
                        storageManager.setDataStorage("fechaRequestCasosEnCurso", fechaAct.toString());
                        if (casos?.status === 200) {
                            storageManager.setDataStorage("casosEnCurso", JSON.stringify(casos?.data));
                            let serviciosStorage = await storageManager.getDataStorage("servicios");
                            let clasificacionesStorage = await storageManager.getDataStorage("clasificaciones");
                            let canalesStorage = await storageManager.getDataStorage("canales");

                            let servicios = JSON.parse(serviciosStorage);
                            let clasificaciones = JSON.parse(clasificacionesStorage);
                            let canales = JSON.parse(canalesStorage);

                            const casosHistorial = await filtrarCasosEnHistorial(casos.data, clasificaciones, servicios, canales);
                            casosFiltrados = await filtrarCasosEnCurso(casosHistorial);

                            dispatchAsistenciaMedica({
                                type: AsistenciaMedicaActionType.actualizarCasosEnCurso,
                                casosEnCurso: casosFiltrados
                            });

                            const socioConClasificaciones = {};
                            casosFiltrados.forEach(caso => {
                                const nombre = caso.principal.nombre;
                                if (!socioConClasificaciones[nombre]) {
                                    socioConClasificaciones[nombre] = {
                                        nombre,
                                        clasificaciones: [],
                                    };
                                }

                                socioConClasificaciones[nombre].clasificaciones.push(caso);
                            });

                            casosPorClasificacion = Object.values(socioConClasificaciones);

                            resolve(casosHistorial);
                        } else {
                            serviciosEnCursoStorage();
                        }
                    } catch (error: any) {
                        console.error(`ASIS-MED - Hubo un problema asociado al llamado de servicios en curso ${error}`);
                        reject(error);
                    }

                    await controlDeVerdes(apiRequest, dispatchAsistenciaMedica, casosPorClasificacion, serviciosActivos);

                    setRequestServicio(false);

                } else {
                    serviciosEnCursoStorage();
                }
            });
        })
    }

    /**
     * Encargado de recuperar los casos en curso del storage si no se cumplio el tiempo del delay para realizar su request
     */
    const serviciosEnCursoStorage = () => {
        let casosEnCurso;
        storageManager.getDataStorage("casosEnCurso").then(casos => {
            casosEnCurso = casos;
            if (casosEnCurso) {
                filtrarCasosEnHistorial(JSON.parse(casosEnCurso), asistenciaMedica.clasificaciones, asistenciaMedica.servicios, asistenciaMedica.canales).then(async casosFiltrados => {
                    const casos = await filtrarCasosEnCurso(casosFiltrados);
                    dispatchAsistenciaMedica({
                        type: AsistenciaMedicaActionType.actualizarCasosEnCurso,
                        casosEnCurso: casos
                    });
                    setRequestServicio(false);
                });
            }
        });
    }

    /**
     * Metodo que verifica la deshabilitacion del modulo a traves del contexto que envia el modulo de ads
     */
    const controlDeVersiones = () => {
        let canalesContacto = {
            canalWhatsapp: true,
            canalTelefono: true,
            redirigirWeb: true
        }
        setDeshabilitarModulos({ ...canalesContacto });
        if (controlVersiones && controlVersiones.asistencia?.infoModulo?.enabled) {
            setDeshabilitarAsistencia(!controlVersiones?.asistencia?.infoModulo?.enabled);
        } else {
            //entra al else cuando controlVersiones es undefined que ocurre cuando falla el servicio o no se pudo completar la peticion, la app se comporta como si estuviera habilitado
            setDeshabilitarAsistencia(false);
        }
        setLlamarServicios(true);
    }

    return {
        flags,
        deshabilitarModulos,
        setDeshabilitarModulos,
        setReintentarRequest
    }
};